import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiCheck, FiInfo, FiPlus, FiImage, FiFileText, FiTrash2, FiChevronDown, FiChevronUp, FiLink, FiExternalLink } from 'react-icons/fi';

const ChecklistItem = ({ item, progressData, onToggle, onUpdateDetails }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');

  // Handle both old boolean format and new object format
  const isCompleted = typeof progressData === 'object' ? progressData.completed : !!progressData;
  const notes = progressData?.notes || '';
  
  // Support both legacy single imageUrl and new imageList array
  const imageList = progressData?.imageList || (progressData?.imageUrl ? [progressData.imageUrl] : []);

  const handleToggle = (e) => {
    e.stopPropagation();
    onToggle();
  };

  const handleAddImage = (e) => {
    e.preventDefault();
    if (!newImageUrl.trim()) return;
    
    const updatedList = [...imageList, newImageUrl.trim()];
    onUpdateDetails({
      ...(typeof progressData === 'object' ? progressData : { completed: !!progressData }),
      imageList: updatedList,
      imageUrl: updatedList[0] // Maintain legacy support
    });
    setNewImageUrl('');
  };

  const handleRemoveImage = (index) => {
    const updatedList = imageList.filter((_, i) => i !== index);
    onUpdateDetails({
      ...(typeof progressData === 'object' ? progressData : { completed: !!progressData }),
      imageList: updatedList,
      imageUrl: updatedList.length > 0 ? updatedList[0] : ''
    });
  };

  const handleNoteUpdate = (value) => {
    onUpdateDetails({
      ...(typeof progressData === 'object' ? progressData : { completed: !!progressData }),
      notes: value
    });
  };

  return (
    <div className={`group flex flex-col rounded-xl border transition-all duration-200 ${isCompleted ? 'bg-cream-dark/20 border-transparent' : 'bg-white border-cream-dark hover:border-sage hover:shadow-sm'}`}>
      <div 
        className="flex items-start gap-4 p-4 cursor-pointer" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div 
          onClick={handleToggle}
          className={`mt-1 flex-shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${isCompleted ? 'bg-sage border-sage text-white' : 'border-espresso-light/30 group-hover:border-sage'}`}
        >
          {isCompleted && <SafeIcon icon={FiCheck} className="w-4 h-4" />}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h4 className={`font-semibold text-lg ${isCompleted ? 'line-through text-espresso-light/60' : 'text-espresso'}`}>
              {item.title}
            </h4>
            {item.specs && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${isCompleted ? 'bg-transparent border-espresso-light/20 text-espresso-light/40' : 'bg-cream border-terracotta/30 text-terracotta'}`}>
                {item.specs}
              </span>
            )}
            {(notes || imageList.length > 0) && !isExpanded && (
              <div className="flex gap-1 items-center">
                {notes && <SafeIcon icon={FiFileText} className="w-3 h-3 text-sage" />}
                {imageList.length > 0 && (
                  <div className="flex items-center gap-0.5 bg-terracotta/10 px-1 rounded">
                    <SafeIcon icon={FiImage} className="w-3 h-3 text-terracotta" />
                    <span className="text-[9px] font-bold text-terracotta">{imageList.length}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          <p className={`text-sm leading-relaxed ${isCompleted ? 'text-espresso-light/40' : 'text-espresso-light'}`}>
            {item.description}
          </p>
        </div>
        <div className="flex-shrink-0 text-espresso-light/30">
          <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} />
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-cream-dark/30 bg-cream/10 animate-fade-in">
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-bold text-espresso-light/60 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                <SafeIcon icon={FiFileText} /> Notes & Data
              </label>
              <textarea 
                className="w-full text-sm bg-white border border-cream-dark rounded-lg p-3 focus:outline-none focus:border-terracotta min-h-[80px] text-espresso"
                placeholder="Add specific details, links, or brainstorming notes here..."
                value={notes}
                onChange={(e) => handleNoteUpdate(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-espresso-light/60 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                <SafeIcon icon={FiImage} /> Assets & References ({imageList.length})
              </label>
              
              <form onSubmit={handleAddImage} className="flex gap-2 mb-3" onClick={e => e.stopPropagation()}>
                <div className="relative flex-1">
                  <SafeIcon icon={FiLink} className="absolute left-3 top-1/2 -translate-y-1/2 text-espresso-light/30 w-3.5 h-3.5" />
                  <input 
                    className="w-full text-sm bg-white border border-cream-dark rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-terracotta text-espresso"
                    placeholder="Paste Image URL..."
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                  />
                </div>
                <button 
                  type="submit"
                  className="bg-espresso text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-espresso-light transition-colors flex items-center gap-2"
                >
                  <SafeIcon icon={FiPlus} /> Add
                </button>
              </form>
              
              {imageList.length > 0 && (
                <div className="grid grid-cols-2 gap-4">
                  {imageList.map((url, index) => (
                    <div key={index} className="flex flex-col gap-1.5">
                      <div className="relative group/img bg-white rounded-lg border border-cream-dark overflow-hidden flex items-center justify-center aspect-video shadow-sm">
                        <img 
                          src={url} 
                          alt={`Reference ${index + 1}`} 
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1594322436404-5a0526db4d13?q=80&w=400&auto=format&fit=crop';
                          }}
                        />
                        <div className="absolute inset-0 bg-espresso/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveImage(index);
                            }}
                            className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                            title="Remove image"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                          <a 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="bg-white text-espresso p-2 rounded-full hover:bg-cream transition-colors"
                            title="Open in new tab"
                          >
                            <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 px-1 overflow-hidden">
                        <SafeIcon icon={FiLink} className="w-2.5 h-2.5 text-espresso-light/30 flex-shrink-0" />
                        <span className="text-[9px] text-espresso-light/40 truncate select-all font-mono" title={url}>
                          {url}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecklistItem;